import { useMemo, useState } from 'react';
import { ChevronRight, KeyRound, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DevicePanel } from '@/components/devices/DevicePanel';
import { InfoDialog, NEARBY_KEY_STEPS } from '@/components/info/InfoDialog';
import { useAllKeys, useRevokeKey } from '@/hooks/useKeys';
import { useGeofenceWatch } from '@/hooks/useGeofenceWatch';
import { useGeofenceStore } from '@/stores/geofenceStore';
import { useDeviceStore } from '@/stores/deviceStore';
import type { DeviceAssignment } from '@/types';

const MAX_CARDS = 3;

function formatDistance(meters: number): string {
  if (meters < 1000) return `~${meters} m`;
  return `~${(meters / 1000).toFixed(1)} km`;
}

interface NearbyKeyReadyProps {
  /**
   * Visa opt-in-/status-/felkorten (Nycklar-fliken). Utan flaggan renderas
   * enbart själva "Nyckel redo"-korten vid träff (Hem).
   */
  showOptIn?: boolean;
}

/**
 * NearbyKeyReady — geofencing-ytan för "Nyckel redo".
 *
 * När funktionen är på och appen är synlig matchas positionen lokalt mot
 * geofences för användarens enheter (se useGeofenceWatch — positionen lämnar
 * aldrig telefonen). Vid träff visas ett kort per enhet (närmast först);
 * tap öppnar befintliga DevicePanel med enhetens nycklar. Att visa kortet är
 * allt funktionen gör — nyckelgenerering/upplåsning förblir en explicit
 * användaråtgärd via panelens ordinarie flöde.
 *
 * Funktionen är foreground-only: utan service worker (och på iOS oavsett)
 * finns ingen bakgrundsbevakning, och det utlovas inte heller i UI:t.
 */
export function NearbyKeyReady({ showOptIn }: NearbyKeyReadyProps) {
  const nearbyKeysEnabled = useGeofenceStore((s) => s.nearbyKeysEnabled);
  const setNearbyKeysEnabled = useGeofenceStore((s) => s.setNearbyKeysEnabled);
  const { assignments, setActiveDevice } = useDeviceStore();
  const { matches, permission, geoError } = useGeofenceWatch();
  const { data: keys } = useAllKeys();
  const revokeKey = useRevokeKey();

  const [panelAssignment, setPanelAssignment] = useState<DeviceAssignment | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const assignmentByDevice = useMemo(() => {
    const map = new Map<string, DeviceAssignment>();
    for (const a of assignments) map.set(a.device_id, a);
    return map;
  }, [assignments]);

  const nearby = useMemo(
    () =>
      matches
        .map((m) => {
          const assignment = assignmentByDevice.get(m.deviceId);
          return assignment ? { assignment, distanceMeters: m.distanceMeters } : null;
        })
        .filter((x): x is { assignment: DeviceAssignment; distanceMeters: number } => !!x)
        .slice(0, MAX_CARDS),
    [matches, assignmentByDevice]
  );

  const openPanel = (assignment: DeviceAssignment) => {
    if (assignment.device) setActiveDevice(assignment.device);
    setPanelAssignment(assignment);
    setPanelOpen(true);
  };

  const handleRevoke = async (keyId: string) => {
    setRevokingId(keyId);
    try {
      await revokeKey.mutateAsync(keyId);
    } finally {
      setTimeout(() => setRevokingId(null), 400);
    }
  };

  // Av-läge: opt-in-kort på Nycklar-fliken, inget alls på Hem.
  if (!nearbyKeysEnabled) {
    if (!showOptIn) return null;
    return (
      <div className="flex items-center gap-3 rounded-2xl glass-thin px-4 py-3 animate-fade-in">
        <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
        <p className="min-w-0 flex-1 text-sm text-muted-foreground">
          Valfritt: visa rätt nyckel automatiskt när du är framme.{' '}
          <InfoDialog title="Nyckel redo när du är framme" steps={NEARBY_KEY_STEPS}>
            <button className="underline underline-offset-2 transition-colors hover:text-foreground">
              Hur funkar det?
            </button>
          </InfoDialog>
        </p>
        {/* Aktivering via user gesture — krävs för platsprompten på iOS. */}
        <Button size="sm" className="shrink-0" onClick={() => setNearbyKeysEnabled(true)}>
          Aktivera
        </Button>
      </div>
    );
  }

  const degraded = permission === 'denied' || permission === 'unavailable';

  return (
    <div className="space-y-3">
      {showOptIn && (
        <div className="flex items-center gap-3 rounded-2xl glass-thin px-4 py-3 animate-fade-in">
          <MapPin
            className={`h-4 w-4 shrink-0 ${degraded ? 'text-muted-foreground' : 'text-primary'}`}
          />
          <p className="min-w-0 flex-1 text-sm text-muted-foreground">
            {degraded
              ? geoError
              : 'Nyckel redo är aktivt — rätt nyckel visas när du är framme.'}
          </p>
          <Button
            size="sm"
            variant="ghost"
            className="shrink-0"
            onClick={() => setNearbyKeysEnabled(false)}
          >
            Stäng av
          </Button>
        </div>
      )}

      {nearby.map(({ assignment, distanceMeters }) => (
        <button
          key={assignment.device_id}
          onClick={() => openPanel(assignment)}
          className="flex w-full items-center gap-3 rounded-2xl glass-thin p-4 text-left transition-all animate-fade-in active:scale-[0.98]"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/15 ring-1 ring-[hsl(var(--glass-stroke))]">
            <KeyRound className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold leading-tight truncate">
              Nyckel redo — {assignment.device?.name ?? 'Enhet'}
            </p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Du är framme ({formatDistance(distanceMeters)}). Tryck för att öppna nyckeln.
            </p>
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
        </button>
      ))}

      <DevicePanel
        assignment={panelAssignment}
        keys={
          panelAssignment
            ? (keys ?? []).filter((k) => k.device_id === panelAssignment.device_id)
            : []
        }
        open={panelOpen}
        onOpenChange={setPanelOpen}
        onRevoke={handleRevoke}
        revokingId={revokingId}
      />
    </div>
  );
}
